import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import DashboardHeader from '../../components/DashboardHeader';
import { useAuth } from '../../context/AuthContext';
import {
  FaSearch,
  FaCheckCircle,
  FaList,
  FaTh,
  FaUser,
  FaMapMarkerAlt,
  FaPhone,
  FaCalendarAlt,
  FaBriefcase,
  FaHospital,
  FaPlus,
  FaEye,
  FaEyeSlash,
  FaCamera,
  FaFileAlt
} from 'react-icons/fa';
import { BiPencil } from 'react-icons/bi';
import '../../App.css';
import client from '../../api/client';
import { toast } from 'react-toastify';
import ReactQuill from "react-quill";

function RegisteredProfessionals() {
  const { user } = useAuth();
  // API data states
  const [professionals, setProfessionals] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [professionTypes, setProfessionTypes] = useState([]);
  const [postings, setPostings] = useState([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingProfessionTypes, setIsLoadingProfessionTypes] = useState(false);
  const [isLoadingPostings, setIsLoadingPostings] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [existingFiles, setExistingFiles] = useState({
    cvName: '',
    cvBytes: '',
    certName: '',
    certBytes: ''
  });

  const [originalEmail, setOriginalEmail] = useState('');
  const [originalMobile, setOriginalMobile] = useState('');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [professionFilter, setProfessionFilter] = useState(null); // idProfessionType
  const [districtFilter, setDistrictFilter] = useState(null); // idDistrict
  const [statusFilter, setStatusFilter] = useState('All');

  // Modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPostingsModal, setShowPostingsModal] = useState(false);
  const [showAddProfessionalModal, setShowAddProfessionalModal] = useState(false);
  const [showEditProfessionalModal, setShowEditProfessionalModal] = useState(false);

  // Selected
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState(null);
  const [editingProfessionalId, setEditingProfessionalId] = useState(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  // Posting filters
  const [postingsFilter, setPostingsFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');

  // Form
  const [professionalFormData, setProfessionalFormData] = useState({
    profession: '',
    name: '',
    address: '',
    gender: '',
    dateOfBirth: '',
    pincode: '',
    qualification: '',
    specialization: '',
    registrationNumber: '',
    experience: '',
    experienceDetails: '',
    mobileNumber: '',
    emailId: '',
    password: '',
    reenterPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);

  const [degrees, setDegrees] = useState([]);
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [isQualificationDisabled, setIsQualificationDisabled] = useState(true);

  const [locationName, setLocationName] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);

  // Files
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [registrationCertificateFile, setRegistrationCertificateFile] = useState(null);

  // Errors
  const [pincodeError, setPincodeError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [contactError, setContactError] = useState('');

  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalPincode, setOriginalPincode] = useState('');
  const [originalDistrict, setOriginalDistrict] = useState({ idDistrict: null, districtName: '' });

  
  // ---------------- Helpers ----------------

  const downloadBase64File = (base64, fileName = 'download') => {
    try {
      if (!base64) return;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Unable to download file.');
    }
  };

  const openBase64InNewTab = (base64, fileName = 'file') => {
    try {
      if (!base64) return;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const lower = (fileName || '').toLowerCase();
      let mime = 'application/octet-stream';
      if (lower.endsWith('.pdf')) mime = 'application/pdf';
      else if (lower.endsWith('.doc')) mime = 'application/msword';
      else if (lower.endsWith('.docx')) mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) mime = 'image/jpeg';
      else if (lower.endsWith('.png')) mime = 'image/png';

      const blob = new Blob([byteArray], { type: mime });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Unable to view file.');
    }
  };

  // Format date for display (DD MMM YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // ---------------- Fetchers ----------------

  const fetchDegrees = async () => {
    try {
      const response = await client.get('/api/professional-degrees');
      setDegrees(response.data?.data || []);
    } catch {
      setDegrees([]);
    }
  };

  const fetchDistricts = async () => {
    setIsLoadingDistricts(true);
    try {
      const response = await client.get('/api/districts');
      setDistricts(response.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load districts.');
      setDistricts([]);
    } finally {
      setIsLoadingDistricts(false);
    }
  };

  const fetchProfessionTypes = async () => {
    setIsLoadingProfessionTypes(true);
    try {
      const response = await client.get('/api/professional-types');
      setProfessionTypes(response.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load profession types.');
      setProfessionTypes([]);
    } finally {
      setIsLoadingProfessionTypes(false);
    }
  };

  const handleUpdateProfessionalStatus = async (professionalId, status) => {
    try {
      const confirmMsg = `Are you sure you want to ${status} this professional?`;
      if (!window.confirm(confirmMsg)) return;
      setIsStatusUpdating(true);
      const payload = {
        professionalID: professionalId,
        status: status
      };
  
      await client.post("/api/healthcare-professionals/status", payload);
  

  
      // ✅ Refresh list

  // ✅ optional delay so user sees update feedback
  setTimeout(() => {
    setShowDetailsModal(false);
    setSelectedProfessional(null);
    setIsStatusUpdating(false);
  }, 800); // you can change to 500 / 1000 etc
  await fetchProfessionals();
  toast.success(`Professional ${status} successfully!`);
    } catch (err) {
      setIsStatusUpdating(false);
      toast.error(err?.response?.data?.message || "Failed to update status.");
    }
  };
  
  
  const fetchProfessionals = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (districtFilter) params.idDistrict = districtFilter;
      if (professionFilter) params.idProfessionType = professionFilter;

      const response = await client.get(
        '/api/healthcare-professionals/professionals/list-with-applications',
        { params }
      );
      setProfessionals(response.data?.data || []);
    } catch (err) {
      setLoadError(err?.response?.data?.message || 'Unable to load professionals.');
      setProfessionals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfessionalPostings = async (professionalId) => {
    setIsLoadingPostings(true);
    try {
      const response = await client.get(
        `/api/healthcare-professionals/professionals/${professionalId}/applied-postings`
      );
      setPostings(response.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load postings.');
      setPostings([]);
    } finally {
      setIsLoadingPostings(false);
    }
  };

  const downloadProfessionalFile = async (professionalId, type) => {
    try {
      const response = await client.get(`/api/healthcare-professionals/current?professionalId=${professionalId}`);
      const data = response.data?.data;
      if (!data) {
        toast.error('Professional not found.');
        return;
      }

      if (type === 'cv') {
        const bytes = data.bioDataFileBytes || data.cvBytes || data.bioDataFileBytes;
        const name = data.bioDataFileName || data.cvName || 'cv.pdf';
        if (!bytes) {
          toast.error('CV not available for this professional.');
          return;
        }
        downloadBase64File(bytes, name);
        return;
      }

      if (type === 'cert') {
        const bytes = data.registrationCertificateFileBytes || data.certBytes;
        const name = data.registrationCertificateFileName || data.certName || 'certificate.pdf';
        if (!bytes) {
          toast.error('Certificate not available for this professional.');
          return;
        }
        downloadBase64File(bytes, name);
        return;
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to download file.');
    } finally {
      // cleaned up unused download state
    }
  };

  // ---------------- Effects ----------------

  useEffect(() => {
    fetchDistricts();
    fetchProfessionTypes();
    fetchDegrees();
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [districtFilter, professionFilter]);

  // Update qualification options when profession changes
  useEffect(() => {
    const professionId = parseInt(professionalFormData.profession, 10);

    if (Number.isFinite(professionId)) {
      const filtered = degrees
        .filter((deg) => deg.idProfessional === professionId)
        .map((deg) => ({
          id: deg.idProfessionalDegree,
          name: deg.professionalDegreeName
        }))
        .filter((d) => d.id && d.name);

      setQualificationOptions(filtered);
      setIsQualificationDisabled(filtered.length === 0);

      const selectedId = parseInt(professionalFormData.qualification, 10);
      const exists = filtered.some((d) => d.id === selectedId);
      if (!exists) {
        setProfessionalFormData((prev) => ({ ...prev, qualification: '' }));
      }
    } else {
      setQualificationOptions([]);
      setIsQualificationDisabled(true);
      setProfessionalFormData((prev) => ({ ...prev, qualification: '' }));
    }
  }, [degrees, professionalFormData.profession]);

  // ---------------- Validations ----------------

  const validatePincode = async () => {
    let message = '';
    setPincodeError('');

    const pincode = (professionalFormData.pincode || '').trim();

    if (pincode.length !== 6) {
      message = 'Enter a 6-digit pincode.';
      setPincodeError(message);
      setLocationName('');
      setIdDistrict(null);
      return { ok: false, message };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(`/api/districts/validate-pincode?pincode=${pincode}`);
      const result = response.data?.data;

      if (result?.districtName) {
        setLocationName(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        return { ok: true, message: '' };
      }

      message = 'Enter a valid pincode.';
      setPincodeError(message);
      setLocationName('');
      setIdDistrict(null);
      return { ok: false, message };
    } catch {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  const validateContact = async (field = 'both') => {
    let message = '';

    if (field === 'both') setContactError('');
    if (field !== 'mobile') setEmailError('');
    if (field !== 'email') setMobileError('');

    const email = (professionalFormData.emailId || '').trim();
    const mobile = (professionalFormData.mobileNumber || '').trim();

    const emailValid = field === 'mobile' ? true : /\S+@\S+\.\S+/.test(email);
    const mobileValid = field === 'email' ? true : mobile.length === 10;

    if (!emailValid || !mobileValid) {
      if (!emailValid && field !== 'mobile') {
        message = 'Enter a valid email.';
        setEmailError(message);
      }
      if (!mobileValid && field !== 'email') {
        message = message || 'Enter a valid 10-digit mobile number.';
        setMobileError('Enter a valid 10-digit mobile number.');
      }
      if (field === 'both' && message) setContactError(message);
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : (email || null),
        mobileNumber: field === 'email' ? null : (mobile || null)
      };

      const response = await client.post('/api/users/validate-contact', payload);
      const exists = response.data?.data;

      if (exists) {
        if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
          setContactError(message);
        }
        return { ok: false, message };
      }

      return { ok: true, message: '' };
    } catch {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'email') setEmailError(message);
      else if (field === 'mobile') setMobileError(message);
      else setContactError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };

  const getProfessionalStatus = (professional) => {
    // ✅ if backend directly sends status
    if (professional.status) return professional.status;
  
    // ✅ if backend sends flags
    if (professional.isRejected) return "Rejected";
    if (professional.isSuspended) return "Suspended";
    if (professional.isApproved) return "Approved";
  
    return "New";
  };
  const validateRequiredFields = () => {
    const data = professionalFormData;

    if (!data.profession) return { ok: false, message: 'Please select profession.' };
    if (!data.qualification) return { ok: false, message: 'Please select qualification.' };
    if (!data.gender) return { ok: false, message: 'Please select gender.' };
    if (!data.dateOfBirth) return { ok: false, message: 'Please select date of birth.' };
    if (!data.name?.trim()) return { ok: false, message: 'Please enter name.' };
    if (!data.address?.trim()) return { ok: false, message: 'Please enter address.' };
    if (!data.experienceDetails?.trim()) return { ok: false, message: 'Please enter experience details.' };

    const dob = new Date(data.dateOfBirth);
    const today = new Date();
    if (Number.isNaN(dob.getTime()) || dob > today) {
      return { ok: false, message: 'Please enter a valid date of birth.' };
    }

    return { ok: true, message: '' };
  };

  // ---------------- UI handlers ----------------

  const handleViewPostings = async (professionalId, filterType = 'all') => {
    setSelectedProfessionalId(professionalId);
    setPostingsFilter(filterType);
    setShowPostingsModal(true);
    await fetchProfessionalPostings(professionalId);
  };
  const handleQuillChange = (value) => {
     const div = document.createElement("div");
  div.innerHTML = value || "";
  const plainText = div.innerText.trim();

  if (plainText.length > 8000) {
    toast.error("Experience Details cannot exceed 8000 characters!");
    return;
  }
    setProfessionalFormData((prev) => ({
      ...prev,
      experienceDetails: value
    }));
  };

  const handleClosePostingsModal = () => {
    setShowPostingsModal(false);
    setSelectedProfessionalId(null);
    setPostingsFilter('all');
    setPostings([]);
  };

  const getFilteredPostings = () => {
    if (postingsFilter === 'completed') {
      return postings.filter((posting) => posting.status === 'Completed' || posting.status === 'Selected');
    }
    return postings;
  };

  const handleAddProfessionalClick = () => {
    setShowAddProfessionalModal(true);
  };

  const handleProfessionalFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'password' || name === 'reenterPassword') {
      if (value.length > 20) {
        toast.error('Password cannot exceed 20 characters!');
        return;
      }
      setProfessionalFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    if (name === 'pincode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setProfessionalFormData((prev) => ({ ...prev, pincode: numericValue }));
      setPincodeError('');
      setLocationName('');
      setIdDistrict(null);
      return;
    }

    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setProfessionalFormData((prev) => ({ ...prev, mobileNumber: numericValue }));
      setMobileError('');
      setContactError('');
      return;
    }

    if (name === 'emailId') {
      setProfessionalFormData((prev) => ({ ...prev, emailId: value }));
      setEmailError('');
      setContactError('');
      return;
    }

    if (name === 'experience') {
      let numValue = parseInt(value, 10);
      if (Number.isNaN(numValue) || numValue < 0) {
        setProfessionalFormData((prev) => ({ ...prev, experience: '' }));
        return;
      }
      if (numValue > 70) numValue = 70;
      setProfessionalFormData((prev) => ({ ...prev, experience: String(numValue) }));
      return;
    }

    setProfessionalFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`${fileType} file size must be less than 5MB!`);
      e.target.value = '';
      return;
    }

    let allowedTypes = [];
    if (fileType === 'CV') {
      allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error('CV must be a PDF, Word document, or image (.pdf, .doc, .docx, .jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
    } else if (fileType === 'Photo') {
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Photo must be a JPEG or PNG image (.jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
    } else if (fileType === 'Registration Certificate') {
      allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Registration Certificate must be a PDF, Word document, or image (.pdf, .doc, .docx, .jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
    }

    if (fileType === 'CV') {
      setCvFile(file);
    } else if (fileType === 'Photo') {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    } else if (fileType === 'Registration Certificate') {
      setRegistrationCertificateFile(file);
    }
  };

  // ---------------- Details / Edit ----------------

  const handleViewProfessional = async (professionalId) => {
    setSelectedProfessional(null);
    setShowDetailsModal(true);
    try {
      const response = await client.get(`/api/healthcare-professionals/current?professionalId=${professionalId}`);
      const data = response.data?.data;

      if (data) {
        const districtName =
          data?.district?.districtName ||
          data?.districtName ||
          districts.find((d) => d.idDistrict === data.idDistrict)?.districtName ||
          '';

        setSelectedProfessional({
          ...data,
          districtName,
          emailId: data.emailID ?? data.emailId ?? '',
          photoSrc: data.photoFileBytes ? `data:image/jpeg;base64,${data.photoFileBytes}` : null,
          cv: { name: data.bioDataFileName, bytes: data.bioDataFileBytes },
          certificate: { name: data.registrationCertificateFileName, bytes: data.registrationCertificateFileBytes }
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load professional details.');
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedProfessional(null);
  };

  const handleEditProfessional = async (professionalId) => {
    setEditingProfessionalId(professionalId);
    try {

      const response = await client.get(`/api/healthcare-professionals/current?professionalId=${professionalId}`);
      const professional = response.data?.data;

      if (professional) {
        setPhotoPreview(professional.photoFileBytes ? `data:image/jpeg;base64,${professional.photoFileBytes}` : null);

        setExistingFiles({
          cvName: professional.bioDataFileName,
          cvBytes: professional.bioDataFileBytes,
          certName: professional.registrationCertificateFileName,
          certBytes: professional.registrationCertificateFileBytes
        });

        setProfessionalFormData({
          profession: professional.idProfessionType ? String(professional.idProfessionType) : '',
          name: professional.professionalName || '',
          address: professional.address || '',
          gender: professional.gender ? professional.gender.toLowerCase() : '',
          dateOfBirth: professional.dateOfBirth ? professional.dateOfBirth.split('T')[0] : '',
          pincode: professional.pincode || '',
          qualification: professional.idProfessionalDegree ? String(professional.idProfessionalDegree) : '',
          specialization: professional.specialization || '',
          registrationNumber: professional.registrationNumber || '',
          experience: professional.yearsOfExperience ? String(professional.yearsOfExperience) : '',
          experienceDetails: professional.experienceDetails || '',
          countryNumber: professional.countryNumber || '',
          mobileNumber: professional.mobileNumber || '',
          emailId: professional.emailID || '',
          password: '',
          reenterPassword: ''
        });


        setOriginalEmail(professional.emailID || '');
        setOriginalMobile(professional.mobileNumber || '');
        setOriginalPincode(professional.pincode || '');

        const districtName =
          professional.pincodeName;
        setOriginalDistrict({
          idDistrict: professional.idDistrict ?? null,
          districtName
        });

        setIdDistrict(professional.idDistrict ?? null);
        setLocationName(districtName || '');

      

        setPincodeError('');
        setMobileError('');
        setEmailError('');
        setContactError('');

        setShowPassword(false);
        setShowReenterPassword(false);
        setPhotoFile(null);
        setCvFile(null);
        setRegistrationCertificateFile(null);
        setShowEditProfessionalModal(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load professional for edit.');
      setEditingProfessionalId(null);
    }
  };

  const handleCloseAddProfessionalModal = () => {
    setShowAddProfessionalModal(false);
    setProfessionalFormData({
      profession: '',
      name: '',
      address: '',
      gender: '',
      dateOfBirth: '',
      pincode: '',
      qualification: '',
      specialization: '',
      registrationNumber: '',
      experience: '',
      experienceDetails: '',
      mobileNumber: '',
      emailId: '',
      password: '',
      reenterPassword: ''
    });

    setLocationName('');
    setIdDistrict(null);

    setPincodeError('');
    setMobileError('');
    setEmailError('');
    setContactError('');

    setShowPassword(false);
    setShowReenterPassword(false);

    setPhotoFile(null);
    setPhotoPreview(null);
    setCvFile(null);
    setRegistrationCertificateFile(null);
  };

  const handleCloseEditProfessionalModal = () => {
    setShowEditProfessionalModal(false);
    setEditingProfessionalId(null);

    setProfessionalFormData({
      profession: '',
      name: '',
      address: '',
      gender: '',
      dateOfBirth: '',
      pincode: '',
      qualification: '',
      specialization: '',
      registrationNumber: '',
      experience: '',
      experienceDetails: '',
      mobileNumber: '',
      emailId: '',
      password: '',
      reenterPassword: ''
    });

    setLocationName('');
    setIdDistrict(null);

    setPincodeError('');
    setMobileError('');
    setEmailError('');
    setContactError('');

    setShowPassword(false);
    setShowReenterPassword(false);

    setPhotoFile(null);
    setPhotoPreview(null);
    setCvFile(null);
    setRegistrationCertificateFile(null);
  };

  // ---------------- Submit handlers ----------------

  const handleProfessionalFormSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const req = validateRequiredFields();
      if (!req.ok) throw new Error(req.message);

      const pincodeResult = await validatePincode();
      if (!pincodeResult.ok) throw new Error(pincodeResult.message || 'Invalid pincode.');

      const mobileResult = await validateContact('mobile');
      if (!mobileResult.ok) throw new Error(mobileResult.message || 'Invalid mobile number.');

      const emailResult = await validateContact('email');
      if (!emailResult.ok) throw new Error(emailResult.message || 'Invalid email.');

      if (professionalFormData.password !== professionalFormData.reenterPassword) {
        throw new Error('Passwords do not match!');
      }
      if (professionalFormData.password.length < 6 || professionalFormData.password.length > 20) {
        throw new Error('Password must be between 6 and 20 characters!');
      }

      const experience = parseInt(professionalFormData.experience, 10);
      if (!Number.isFinite(experience) || experience < 0 || experience > 70) {
        throw new Error('Experience must be between 0 and 70 years!');
      }

      if (!cvFile) throw new Error('Please upload your CV!');
      if (!photoFile) throw new Error('Please upload your photo!');
      if (!registrationCertificateFile) throw new Error('Please upload your registration certificate!');
      if (!locationName || !idDistrict) throw new Error('Enter a valid pincode to fetch location.');

      const email = (professionalFormData.emailId || '').trim();
      const mobile = (professionalFormData.mobileNumber || '').trim();
      const countryNumber = (professionalFormData.countryNumber || '+91').trim();

      const payload = new FormData();
      payload.append('EmailID', email);
      payload.append('MobileNumber', mobile);
      payload.append('CountryNumber', countryNumber);
      payload.append('Password', professionalFormData.password);
      payload.append('IdProfessionType', professionalFormData.profession);
      payload.append('ProfessionalName', professionalFormData.name.trim());
      payload.append('IdProfessionalDegree', professionalFormData.qualification);
      payload.append('Address', professionalFormData.address.trim());
      payload.append('IdDistrict', idDistrict ?? 0);
      payload.append('Pincode', (professionalFormData.pincode || '').trim());
      payload.append('PincodeName', locationName);
      payload.append('Specialization', professionalFormData.specialization || '');
      payload.append('RegistrationNumber', professionalFormData.registrationNumber || '');
      payload.append('DateOfBirth', professionalFormData.dateOfBirth);
      payload.append('Gender', professionalFormData.gender);
      payload.append('YearsOfExperience', String(experience));
      payload.append('ExperienceDetails', professionalFormData.experienceDetails.trim());
      payload.append('IsSuspended', false);

      payload.append('BioDataFile', cvFile);
      payload.append('PhotoFile', photoFile);
      payload.append('RegistrationCertificateFile', registrationCertificateFile);

      await client.post('/api/healthcare-professionals', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Professional added successfully!');
      handleCloseAddProfessionalModal();
      fetchProfessionals();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.message || 'Failed to add professional.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProfessionalFormSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const req = validateRequiredFields();
      if (!req.ok) {
        toast.error(req.message);
        return;
      }

      const curPin = (professionalFormData.pincode || '').trim();
      const origPin = (originalPincode || '').trim();
      const pincodeChanged = curPin !== origPin;

      if (pincodeChanged) {
        const pinResult = await validatePincode();
        if (!pinResult.ok || !idDistrict) {
          toast.error(pinResult.message || 'Please enter a valid pincode.');
          return;
        }
      } else {
        // ensure district exists for payload if unchanged
        setLocationName(originalDistrict.districtName || '');
        setIdDistrict(originalDistrict.idDistrict ?? null);
      }

      const mobile = (professionalFormData.mobileNumber || '').trim();
      const email = (professionalFormData.emailId || '').trim();

      if (mobile.length !== 10) {
        toast.error('Enter a valid 10-digit mobile number.');
        return;
      }
      if (!/\S+@\S+\.\S+/.test(email)) {
        toast.error('Enter a valid email.');
        return;
      }

      const mobileChanged = mobile !== (originalMobile || '').trim();
      const emailChanged =
        email.toLowerCase() !== (originalEmail || '').trim().toLowerCase();

      if (mobileChanged) {
        const mobileResult = await validateContact('mobile');
        if (!mobileResult.ok) {
          toast.error(mobileResult.message || 'Invalid mobile number.');
          return;
        }
      }

      if (emailChanged) {
        const emailResult = await validateContact('email');
        if (!emailResult.ok) {
          toast.error(emailResult.message || 'Invalid email.');
          return;
        }
      }

      if (professionalFormData.password || professionalFormData.reenterPassword) {
        if (professionalFormData.password !== professionalFormData.reenterPassword) {
          toast.error('Passwords do not match!');
          return;
        }
        if (professionalFormData.password.length < 6 || professionalFormData.password.length > 20) {
          toast.error('Password must be between 6 and 20 characters!');
          return;
        }
      }

      const experience = parseInt(professionalFormData.experience, 10);
      if (!Number.isFinite(experience) || experience < 0 || experience > 70) {
        toast.error('Experience must be between 0 and 70 years!');
        return;
      }

      const countryNumber = (professionalFormData.countryNumber || '+91').trim();

      const payload = new FormData();
      payload.append('ProfessionalID', editingProfessionalId);
      payload.append('EmailID', email);
      payload.append('MobileNumber', mobile);
      payload.append('CountryNumber', countryNumber);

      if (professionalFormData.password) payload.append('Password', professionalFormData.password);

      payload.append('IdProfessionType', professionalFormData.profession);
      payload.append('ProfessionalName', professionalFormData.name.trim());
      payload.append('IdProfessionalDegree', professionalFormData.qualification);
      payload.append('Address', professionalFormData.address.trim());

      payload.append('IdDistrict', idDistrict ?? originalDistrict.idDistrict ?? 0);
      payload.append('Pincode', (professionalFormData.pincode || '').trim());
      payload.append('PincodeName', locationName);

      payload.append('Specialization', professionalFormData.specialization || '');
      payload.append('RegistrationNumber', professionalFormData.registrationNumber || '');
      payload.append('DateOfBirth', professionalFormData.dateOfBirth);
      payload.append('Gender', professionalFormData.gender);

      payload.append('YearsOfExperience', String(experience));
      payload.append('ExperienceDetails', professionalFormData.experienceDetails.trim());
      payload.append('IsSuspended', false);

      if (cvFile) payload.append('BioDataFile', cvFile);
      if (photoFile) payload.append('PhotoFile', photoFile);
      if (registrationCertificateFile) payload.append('RegistrationCertificateFile', registrationCertificateFile);

      await client.put('/api/healthcare-professionals', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Professional updated successfully!');
      handleCloseEditProfessionalModal();
      fetchProfessionals();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Update failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------- Derived ----------------

  const filteredProfessionals = professionals.filter((professional) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!(professional.professionalName || '').toLowerCase().includes(term)) return false;
    }
  
    if (statusFilter && statusFilter !== "All") {
      return getProfessionalStatus(professional) === statusFilter;
    }
  
    return true;
  });

  const uniqueStatuses = ['All','Pending', 'Approved', 'Suspended', 'Rejected'];

  const getStatusBadgeClass = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      // Professional Statuses
      case 'approved':
        return 'badge bg-success';
      case 'assigned':
        return 'badge bg-info';
      case 'suspended':
        return 'badge bg-warning text-dark';
      case 'rejected':
        return 'badge bg-danger';
      // Posting Statuses
      case 'completed':
      case 'selected':     
        return 'badge bg-success';
      case 'applied':
      case 'pending':
      case 'open':
        return 'badge bg-primary';
      case 'deleted':
        return 'badge bg-danger text-dark';
      default:
        return 'badge bg-secondary';
    }
  };

  const addressCount = professionalFormData.address.length;
  const getPlainTextLength = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html || "";
    return div.innerText.trim().length;
  };
  
  const experienceCount = getPlainTextLength(professionalFormData.experienceDetails);

  const passwordsMatch =
    professionalFormData.password === professionalFormData.reenterPassword &&
    professionalFormData.reenterPassword !== '';

  // ---------------- Render ----------------

  return (
    <div className="App">
      <DashboardHeader userType="admin" userName={user?.displayName || 'Admin'} />

      <main className="dashboard-content">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
                <h2 className="dashboard-title mb-0">List of Professionals</h2>
                <div className="d-block d-md-flex justify-content-between align-items-center">
                  <div className="btn-group me-3" role="group" aria-label="View Toggle">
                    <button
                      type="button"
                      className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('table')}
                      title="Table View"
                    >
                      <FaList />
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <FaTh />
                    </button>
                  </div>
                  <button className="btn btn-primary" onClick={handleAddProfessionalClick}>
                    <FaPlus className="me-1" /> Add Professional
                  </button>
                </div>
              </div>

              {/* Filter Section */}
              <div className="mb-4">
                <div className="row align-items-end">
                  <div className="col-md-3 mb-3 mb-md-0">
                    <label htmlFor="search" className="form-label">
                      Search
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FaSearch />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="search"
                        placeholder="Search by Professional Name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3 mb-md-0">
                    <label htmlFor="districtFilter" className="form-label">
                      District
                    </label>
                    <select
                      className="form-select"
                      id="districtFilter"
                      value={districtFilter || 'All'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDistrictFilter(value === 'All' ? null : Number(value));
                      }}
                      disabled={isLoadingDistricts}
                    >
                      <option value="All">All Districts</option>
                      {districts.map((district) => (
                        <option key={district.idDistrict} value={district.idDistrict}>
                          {district.districtName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3 mb-3 mb-md-0">
                    <label htmlFor="professionFilter" className="form-label">
                      Profession
                    </label>
                    <select
                      className="form-select"
                      id="professionFilter"
                      value={professionFilter || 'All'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setProfessionFilter(value === 'All' ? null : Number(value));
                      }}
                      disabled={isLoadingProfessionTypes}
                    >
                      <option value="All">All Professions</option>
                      {professionTypes.map((prof) => (
                        <option key={prof.idProfessionType} value={prof.idProfessionType}>
                          {prof.professionTypeName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3 mb-3 mb-md-0">
                    <label htmlFor="statusFilter" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="statusFilter"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      {uniqueStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Professionals Table View */}
              {viewMode === 'table' && (
                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Sl#</th>
                            <th scope="col">Name of Professional</th>
                            <th scope="col">Date Registered</th>
                            <th scope="col">Address</th>
                            <th scope="col">Qualification</th>
                            <th scope="col">Experience</th>
                            <th scope="col">Mobile No.</th>
                            <th scope="col" className="text-center">
                              No of Posting applied
                            </th>
                            <th scope="col" className="text-center">
                              Completed Postings
                            </th>
                            <th scope="col">Status</th>                        
                            <th scope="col" style={{ width: "110px" }} className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="11" className="text-center">
                                Loading...
                              </td>
                            </tr>
                          ) : loadError ? (
                            <tr>
                              <td colSpan="11" className="text-center text-danger">
                                {loadError}
                              </td>
                            </tr>
                          ) : filteredProfessionals.length === 0 ? (
                            <tr>
                              <td colSpan="11" className="text-center">
                                No professionals found
                              </td>
                            </tr>
                          ) : (
                            filteredProfessionals.map((professional, index) => {
                              const status = getProfessionalStatus(professional);
                              return (
                                <tr key={professional.professionalID}>
                                  <td>{index + 1}</td>
                                  <td className="fw-bold">{professional.professionalName}</td>
                                  <td>{formatDate(professional.dateRegistered)}</td>
                                  <td>
                                    {professional.address}
                                    <br />
                                    <small className="text-muted">Pincode: {professional.pincode}</small>
                                  </td>
                                  <td>{professional.professionalDegreeName}</td>
                                  <td>{professional.yearsOfExperience ? `${professional.yearsOfExperience} Years` : '-'}</td>
                                  <td>{professional.mobileNumber}</td>
                                  <td className="text-center">
                                    <a
                                      href="#"
                                      className="text-primary text-decoration-none"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewPostings(professional.professionalID, 'all');
                                      }}
                                    >
                                      {professional.noOfPostingApplied || 0}
                                    </a>
                                  </td>
                                  <td className="text-center">
                                    <a
                                      href="#"
                                      className="text-primary text-decoration-none"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewPostings(professional.professionalID, 'completed');
                                      }}
                                    >
                                      {professional.completedPostings || 0}
                                    </a>
                                  </td>
                                  <td>
                                    <span className={`badge ${getStatusBadgeClass(status)}`}>{status}</span>
                                  </td>
                                  <td>
                                  
                                    <button
                                      type="button"
                                      className="btn btn-link btn-outline-primary p-1 px-2 me-1"
                                      title="Edit professional details"
                                      onClick={() => handleEditProfessional(professional.professionalID)}
                                    >
                                      <BiPencil />
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-link btn-outline-primary p-1 px-2"
                                      title="View professional details"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewProfessional(professional.professionalID);
                                      }}
                                    >
                                      <FaEye />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Professionals Grid View */}
              {viewMode === 'grid' && (
                <div className="row g-3">
                  {filteredProfessionals.length === 0 ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">No professionals found</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    filteredProfessionals.map((professional) => {
                      const status = getProfessionalStatus(professional);
                      const professionName =
                        professionTypes.find((p) => p.idProfessionType === professional.idProfessionType)?.professionTypeName || '-';

                      return (
                        <div key={professional.professionalID} className="col-12 col-md-6 col-lg-4">
                          <div className="card h-100 opportunity-card modern-card">
                            <div className="card-body d-flex flex-column p-0">
                              <div className="card-header-section">
                                <h5 className="card-position-title">{professional.professionalName}</h5>
                                <div className="d-flex align-items-center gap-2">
                                  <span className={`badge ${getStatusBadgeClass(status)}`}>{status}</span>
                                  
                                  <button
                                    type="button"
                                    className="btn btn-link btn-outline-primary p-1 px-2"
                                    title="Edit professional details"
                                    onClick={() => handleEditProfessional(professional.professionalID)}
                                  >
                                    <BiPencil />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-outline-primary p-1 px-2"
                                    title="View professional details"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewProfessional(professional.professionalID);
                                    }}
                                  >
                                    <FaEye />
                                  </button>
                                </div>
                              </div>

                              <div className="card-info-section">
                                <div className="info-item">
                                  <div className="info-icon hospital-icon">
                                    <FaCalendarAlt />
                                  </div>
                                  <div className="info-content">
                                    <div className="info-label">Date Registered</div>
                                    <div className="info-value">{formatDate(professional.dateRegistered)}</div>
                                  </div>
                                </div>

                                <div className="info-item">
                                  <div className="info-icon location-icon">
                                    <FaMapMarkerAlt />
                                  </div>
                                  <div className="info-content">
                                    <div className="info-label">Address</div>
                                    <div className="info-value">
                                      {professional.address}
                                      <br />
                                      <small className="text-muted">Pincode: {professional.pincode}</small>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="card-divider"></div>

                              <div className="card-details-section">
                                <div className="detail-item">
                                  <FaBriefcase className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Experience</span>
                                    <span className="detail-value">
                                      {professional.yearsOfExperience ? `${professional.yearsOfExperience} Years` : '-'}
                                    </span>
                                  </div>
                                </div>

                                <div className="detail-item">
                                  <FaPhone className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Mobile</span>
                                    <span className="detail-value">{professional.mobileNumber}</span>
                                  </div>
                                </div>

                                <div className="detail-item">
                                  <FaUser className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Profession</span>
                                    <span className="detail-value">{professionName}</span>
                                  </div>
                                </div>

                                <div className="detail-item">
                                  <FaHospital className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Applied Postings</span>
                                    <span className="detail-value">
                                      <a
                                        href="#"
                                        className="text-decoration-none"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleViewPostings(professional.professionalID, 'all');
                                        }}
                                      >
                                        {professional.noOfPostingApplied || 0}
                                      </a>
                                    </span>
                                  </div>
                                </div>

                                <div className="detail-item">
                                  <FaCheckCircle className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Completed</span>
                                    <span className="detail-value">
                                      <a
                                        href="#"
                                        className="text-decoration-none"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleViewPostings(professional.professionalID, 'completed');
                                        }}
                                      >
                                        {professional.completedPostings || 0}
                                      </a>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

         {/* Professional Details Modal */}
         <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} centered size="lg" scrollable backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title className="h5">Professional Registration Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProfessional && (
            <div className="row m-0">
              <div className="col-12 mb-4 text-center">
                {selectedProfessional.photoSrc ? (
                  <img
                    src={selectedProfessional.photoSrc}
                    alt="Photo"
                    style={{
                      width: 150,
                      height: 150,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid #dee2e6'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 150,
                      height: 150,
                      borderRadius: '50%',
                      background: '#f8f9fa',
                      border: '3px solid #dee2e6',
                      lineHeight: '144px',
                      color: '#6c757d',
                      margin: 'auto'
                    }}
                  >
                    No Image
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Your Profession</label>
                <p>{selectedProfessional.professionTypeName || '-'}</p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Name</label>
                <p>{selectedProfessional.professionalName || '-'}</p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Gender</label>
                <p>
                  {selectedProfessional.gender
                    ? selectedProfessional.gender.charAt(0).toUpperCase() + selectedProfessional.gender.slice(1)
                    : '-'}
                </p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Date of Birth</label>
                <p>{selectedProfessional.dateOfBirth ? formatDate(selectedProfessional.dateOfBirth) : '-'}</p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Address</label>
                <p>{selectedProfessional.address || '-'}</p>
              </div>

              {/* ✅ PINCODE + DISTRICT */}
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Pincode</label>
                <p>
                  {selectedProfessional.pincode || '-'}
                  <br />
                  <small className="text-muted">
                    District: {selectedProfessional.districtName || '-'}
                  </small>
                </p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Qualification</label>
                <p>{selectedProfessional.professionalDegreeName || '-'}</p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Specialization</label>
                <p>{selectedProfessional.specialization || '-'}</p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Registration Number</label>
                <p>{selectedProfessional.registrationNumber || '-'}</p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">CV (Resume)</label>
                {selectedProfessional.cv?.bytes ? (
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => openBase64InNewTab(selectedProfessional.cv.bytes, selectedProfessional.cv.name || 'CV')}
                    >
                      <FaEye className="me-1" />
                      View CV
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-primary text-decoration-none"
                      onClick={() => downloadBase64File(selectedProfessional.cv.bytes, selectedProfessional.cv.name)}
                    >
                      <FaFileAlt className="me-1" />
                      <span className="file-name-truncate" title={selectedProfessional.cv.name || 'Download CV'}>
                        {selectedProfessional.cv.name || 'Download CV'}
                      </span>
                    </button>
                  </div>
                ) : (
                  <p className="text-muted">-</p>
                )}
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Registration Certificate</label>
                {selectedProfessional.certificate?.bytes ? (
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => openBase64InNewTab(selectedProfessional.certificate.bytes, selectedProfessional.certificate.name || 'Certificate')}
                    >
                      <FaEye className="me-1" />
                      View Certificate
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-primary text-decoration-none"
                      onClick={() => downloadBase64File(selectedProfessional.certificate.bytes, selectedProfessional.certificate.name)}
                    >
                      <FaFileAlt className="me-1" />
                      <span className="file-name-truncate" title={selectedProfessional.certificate.name || 'Download Certificate'}>
                        {selectedProfessional.certificate.name || 'Download Certificate'}
                      </span>
                    </button>
                  </div>
                ) : (
                  <p className="text-muted">-</p>
                )}
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Experience (In Years)</label>
                <p>{selectedProfessional.yearsOfExperience || '-'}</p>
              </div>

              <div className="col-md-12 mb-2">
                <label className="form-label fw-bold">Experience Details</label>     

               <ReactQuill
                        theme="snow"
                        value={selectedProfessional.experienceDetails}                        
                        placeholder="Enter your experience details"
                        className="experience-details-editor"
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'indent': '-1' }, { 'indent': '+1' }],
                            ['link'],
                            ['clean']
                          ]
                        }}
                        readOnly
                        disabled
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet', 'indent',
                          'link'
                        ]}
                        style={{
                          minHeight: '150px',
                          marginBottom: '10px'
                        }}
                      /> 
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Mobile Number</label>
                <p>{selectedProfessional.mobileNumber || '-'}</p>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold">Email ID</label>
                <p>{selectedProfessional.emailId || '-'}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="professional-view-footer">
  <button
    type="button"
    className="btn btn-success footer-btn"
    disabled={isStatusUpdating}
    onClick={() =>
      handleUpdateProfessionalStatus(selectedProfessional.professionalID, "Approved")
    }
  >
    {isStatusUpdating ? "Processing..." : "Approve"}
  </button>

  <button
    type="button"
    className="btn btn-warning text-dark footer-btn"
    disabled={isStatusUpdating}
    onClick={() =>
      handleUpdateProfessionalStatus(selectedProfessional.professionalID, "Suspended")
    }
  >
    {isStatusUpdating ? "Processing..." : "Suspend"}
  </button>

  <button
    type="button"
    className="btn btn-danger footer-btn"
    disabled={isStatusUpdating}
    onClick={() =>
      handleUpdateProfessionalStatus(selectedProfessional.professionalID, "Rejected")
    }
  >
    {isStatusUpdating ? "Processing..." : "Reject"}
  </button>

  <button
    type="button"
    className="btn btn-secondary footer-btn"
    disabled={isStatusUpdating}
    onClick={handleCloseDetailsModal}
  >
    Close
  </button>
</Modal.Footer>



      </Modal>

      {/* Professional Postings Modal */}
      <Modal show={showPostingsModal} onHide={handleClosePostingsModal} centered size="xl" scrollable backdrop="static" keyboard={false} >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            Applied Postings
            {selectedProfessionalId && (
              <span className="ms-2 text-muted" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                - {professionals.find((p) => p.professionalID === selectedProfessionalId)?.professionalName}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProfessionalId &&
            (() => {
              const filteredPostings = getFilteredPostings();
              return (
                <>
                  <div className="d-flex gap-2 mb-3">
                    <button
                      className={`btn btn-sm ${postingsFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setPostingsFilter('all')}
                      type="button"
                    >
                      All Postings Applied
                    </button>
                    <button
                      className={`btn btn-sm ${postingsFilter === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setPostingsFilter('completed')}
                      type="button"
                    >
                      Completed Postings
                    </button>
                  </div>

                  {isLoadingPostings ? (
                    <div className="text-center py-4">
                      <p className="text-muted">Loading...</p>
                    </div>
                  ) : filteredPostings.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No postings found</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Sl#</th>
                            <th scope="col">Hospital Name</th>
                            <th scope="col">Requirement</th>
                            <th scope="col">Posted Date</th>
                            <th scope="col">Applied Date</th>
                            <th scope="col">Period</th>
                            <th scope="col">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPostings.map((posting) => (
                            <tr key={posting.applicationID || posting.jobID}>
                              <td>{posting.slNo || '-'}</td>
                              <td>{posting.hospitalName}</td>
                              <td>{posting.requirement}</td>
                              <td>{formatDate(posting.postedDate)}</td>
                              <td>{formatDate(posting.appliedDate)}</td>
                              <td>{posting.period || '-'}</td>
                              <td>
                                <span className={getStatusBadgeClass(posting.status || '')}>
                                  {posting.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={handleClosePostingsModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Add Professional Modal */}
      <Modal
        show={showAddProfessionalModal}
        onHide={isSubmitting ? undefined : handleCloseAddProfessionalModal}
        centered
        size="xl"
        scrollable
        backdrop="static" keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Add Professional</Modal.Title>
        </Modal.Header>

        <form onSubmit={handleProfessionalFormSubmit}>
          <Modal.Body>
            <div className="row m-0">
              <div className="col-md-12 p-2">
                <div className="photo-upload-container">
                  <div className="photo-preview-wrapper">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Profile preview" className="photo-preview-circle" />
                    ) : (
                      <div className="photo-placeholder-circle">
                        <FaCamera className="photo-placeholder-icon" />
                      </div>
                    )}
                    <label htmlFor="photoFile" className="photo-upload-overlay">
                      <FaCamera className="camera-icon" />
                    </label>
                    <input
                      type="file"
                      className="photo-file-input"
                      id="photoFile"
                      name="photoFile"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'Photo')}
                      required
                    />
                  </div>

                  {photoFile && (
                    <small className="form-text text-success d-block mt-2 text-center">
                      ✓ {photoFile.name} ({formatFileSize(photoFile.size)})
                    </small>
                  )}

                  <small className="form-text text-muted d-block mt-1 text-center">
                    JPG, JPEG, PNG (Max 5MB)
                  </small>
                </div>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="profession" className="form-label">
                  Your Profession <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="profession"
                  name="profession"
                  value={professionalFormData.profession}
                  onChange={handleProfessionalFormChange}
                  required
                >
                  <option value="">Select Profession</option>
                  {professionTypes.map((p) => (
                    <option key={p.idProfessionType} value={p.idProfessionType}>
                      {p.professionTypeName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="name" className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  value={professionalFormData.name}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter your full name"
                  maxLength="60"
                  required
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="gender" className="form-label">
                  Gender <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="gender"
                  name="gender"
                  value={professionalFormData.gender}
                  onChange={handleProfessionalFormChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="dateOfBirth" className="form-label">
                  Date of Birth <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={professionalFormData.dateOfBirth}
                  onChange={handleProfessionalFormChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="address" className="form-label">
                  Address <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  id="address"
                  name="address"
                  rows="3"
                  value={professionalFormData.address}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter your address"
                  maxLength="200"
                  required
                />
                <small className="form-text text-muted">
                  <span id="addressCount">{addressCount}</span>/200 characters
                </small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="pincode" className="form-label">
                  Pincode <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control w-25"
                    id="pincode"
                    name="pincode"
                    value={professionalFormData.pincode}
                    onChange={handleProfessionalFormChange}
                    onBlur={validatePincode}
                    placeholder="Pincode"
                    maxLength="6"
                    pattern="[0-9]{6}"
                    required
                  />
                  <input
                    type="text"
                    className="form-control w-75 form-control-display-only"
                    id="location"
                    name="location"
                    value={locationName}
                    placeholder="Location"
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                </div>
                {pincodeError && <small className="form-text text-danger">{pincodeError}</small>}
                {isValidatingPincode && <small className="form-text text-muted">Validating pincode...</small>}
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="qualification" className="form-label">
                  Qualification <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="qualification"
                  name="qualification"
                  value={professionalFormData.qualification}
                  onChange={handleProfessionalFormChange}
                  required
                  disabled={isQualificationDisabled}
                >
                  <option value="">Select Qualification</option>
                  {qualificationOptions.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.name}
                    </option>
                  ))}
                </select>
                {isQualificationDisabled && (
                  <small className="form-text text-muted">Please select your profession first</small>
                )}
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="specialization" className="form-label">
                  Specialization
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="specialization"
                  name="specialization"
                  value={professionalFormData.specialization}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter your specialization"
                  maxLength="100"
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="registrationNumber" className="form-label">
                  Registration Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="registrationNumber"
                  name="registrationNumber"
                  value={professionalFormData.registrationNumber}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter registration number"
                  maxLength="30"
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="cvFile" className="form-label">
                  CV (Resume) <span className="text-danger">*</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="cvFile"
                  name="cvFile"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'CV')}
                  required
                />
                {cvFile && (
                  <small className="form-text text-success d-block mt-1">
                    ✓ Selected: {cvFile.name} ({formatFileSize(cvFile.size)})
                  </small>
                )}
                <small className="form-text text-muted">Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)</small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="registrationCertificateFile" className="form-label">
                  Registration Certificate <span className="text-danger">*</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="registrationCertificateFile"
                  name="registrationCertificateFile"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'Registration Certificate')}
                  required
                />
                {registrationCertificateFile && (
                  <small className="form-text text-success d-block mt-1">
                    ✓ Selected: {registrationCertificateFile.name} ({formatFileSize(registrationCertificateFile.size)})
                  </small>
                )}
                <small className="form-text text-muted">Accepted formats: PDF, JPG, JPEG, PNG (Max 5MB)</small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="experience" className="form-label">
                  Experience (In Years) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="experience"
                  name="experience"
                  value={professionalFormData.experience}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter years of experience"
                  min="0"
                  max="70"
                  required
                />
              </div>

              <div className="col-md-12 p-2">
                <label htmlFor="experienceDetails" className="form-label">
                  Experience Details <span className="text-danger">*</span>
                </label>             
              

<ReactQuill
                        theme="snow"                                           
                        placeholder="Enter your experience details"
                        className="experience-details-editor"
                        value={professionalFormData.experienceDetails}
                        onChange={handleQuillChange}
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'indent': '-1' }, { 'indent': '+1' }],
                            ['link'],
                            ['clean']
                          ]
                        }}                        
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet', 'indent',
                          'link'
                        ]}
                        style={{
                          minHeight: '150px',
                          marginBottom: '10px'
                        }}
                      /> 
                <small className="form-text text-muted">
                  <span id="experienceCount">{experienceCount}</span>/8000 characters
                </small>
              </div>

             

              <div className="col-md-6 p-2">
                <label htmlFor="mobileNumber" className="form-label">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                 <div className="input-group">
    {/* Small Country Code */}
    <input
      type="text"
      className="form-control"
      style={{ maxWidth: "90px" }}
      id="countryCode"
      name="countryNumber"
      value={professionalFormData.countryNumber}
      maxLength={5}
      onChange={handleProfessionalFormChange}
      placeholder="+91"
      required
    />

    {/* Mobile */}
    <input
      type="tel"
      className="form-control"
      id="mobileNumber"
      name="mobileNumber"
      value={professionalFormData.mobileNumber}
      onChange={handleProfessionalFormChange}
      onBlur={() => validateContact("mobile")}
      placeholder="Enter 10-digit mobile number"
      maxLength="10"
      pattern="[0-9]{10}"
      required
    />
  </div>
                {mobileError && <small className="form-text text-danger">{mobileError}</small>}
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="emailId" className="form-label">
                  Email ID <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="emailId"
                  name="emailId"
                  value={professionalFormData.emailId}
                  onChange={handleProfessionalFormChange}
                  onBlur={() => validateContact('email')}
                  placeholder="Enter your email ID"
                  maxLength="50"
                  required
                />
                {emailError && <small className="form-text text-danger">{emailError}</small>}
                {contactError && <small className="form-text text-danger">{contactError}</small>}
                {isValidatingContact && <small className="form-text text-muted">Validating contact...</small>}
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="password" className="form-label">
                  Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    id="password"
                    name="password"
                    value={professionalFormData.password}
                    onChange={handleProfessionalFormChange}
                    placeholder="Enter password (6-20 characters)"
                    minLength="6"                 
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <small className="form-text text-muted">Password must be 6-20 characters long</small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="reenterPassword" className="form-label">
                  Re-enter Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type={showReenterPassword ? 'text' : 'password'}
                    className={`form-control ${professionalFormData.reenterPassword && !passwordsMatch
                        ? 'is-invalid'
                        : professionalFormData.reenterPassword && passwordsMatch
                          ? 'is-valid'
                          : ''
                      }`}
                    id="reenterPassword"
                    name="reenterPassword"
                    value={professionalFormData.reenterPassword}
                    onChange={handleProfessionalFormChange}
                    placeholder="Re-enter password"
                    minLength="6"                  
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setShowReenterPassword(!showReenterPassword)}
                  >
                    {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {professionalFormData.reenterPassword && !passwordsMatch && (
                  <div className="invalid-feedback d-block">Passwords do not match</div>
                )}
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseAddProfessionalModal}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || isValidatingPincode || isValidatingContact}
            >
              {isSubmitting ? 'Adding...' : 'Add Professional'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Edit Professional Modal */}
      <Modal show={showEditProfessionalModal} onHide={handleCloseEditProfessionalModal} centered size="xl" scrollable backdrop="static" keyboard={false} >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Edit Professional</Modal.Title>
        </Modal.Header>

        <form onSubmit={handleEditProfessionalFormSubmit}>
          <Modal.Body>
            <div className="row m-0">
              <div className="col-md-12 p-2">
                <div className="photo-upload-container">
                  <div className="photo-preview-wrapper">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Profile preview" className="photo-preview-circle" />
                    ) : (
                      <div className="photo-placeholder-circle">
                        <FaCamera className="photo-placeholder-icon" />
                      </div>
                    )}
                    <label htmlFor="editPhotoFile" className="photo-upload-overlay">
                      <FaCamera className="camera-icon" />
                    </label>
                    <input
                      type="file"
                      className="photo-file-input"
                      id="editPhotoFile"
                      name="photoFile"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'Photo')}
                    />
                  </div>

                  {photoFile && (
                    <small className="form-text text-success d-block mt-2 text-center">
                      ✓ {photoFile.name} ({formatFileSize(photoFile.size)})
                    </small>
                  )}

                  <small className="form-text text-muted d-block mt-1 text-center">
                    JPG, JPEG, PNG (Max 5MB)
                  </small>
                </div>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editProfession" className="form-label">
                  Your Profession <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="editProfession"
                  name="profession"
                  value={professionalFormData.profession}
                  onChange={handleProfessionalFormChange}
                  required
                >
                  <option value="">Select Profession</option>
                  {professionTypes.map((p) => (
                    <option key={p.idProfessionType} value={p.idProfessionType}>
                      {p.professionTypeName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editName" className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="editName"
                  name="name"
                  value={professionalFormData.name}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter your full name"
                  maxLength="60"
                  required
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editGender" className="form-label">
                  Gender <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="editGender"
                  name="gender"
                  value={professionalFormData.gender}
                  onChange={handleProfessionalFormChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editDateOfBirth" className="form-label">
                  Date of Birth <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="editDateOfBirth"
                  name="dateOfBirth"
                  value={professionalFormData.dateOfBirth}
                  onChange={handleProfessionalFormChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editAddress" className="form-label">
                  Address <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  id="editAddress"
                  name="address"
                  rows="3"
                  value={professionalFormData.address}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter your address"
                  maxLength="200"
                  required
                />
                <small className="form-text text-muted">
                  <span id="editAddressCount">{addressCount}</span>/200 characters
                </small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editPincode" className="form-label">
                  Pincode <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control w-25"
                    id="editPincode"
                    name="pincode"
                    value={professionalFormData.pincode}
                    onChange={handleProfessionalFormChange}
                    onBlur={() => {
                      const cur = (professionalFormData.pincode || '').trim();
                      const orig = (originalPincode || '').trim();

                      if (cur.length === 6 && cur !== orig) {
                        validatePincode();
                      } else if (cur === orig) {
                        // restore old district values without API
                        setLocationName(originalDistrict.districtName || '');
                        setIdDistrict(originalDistrict.idDistrict ?? null);
                        setPincodeError('');
                      }
                    }}
                    placeholder="Pincode"
                    maxLength="6"
                    pattern="[0-9]{6}"
                    required
                  />
                  <input
                    type="text"
                    className="form-control w-75 form-control-display-only"
                    id="editLocation"
                    name="location"
                    value={locationName}
                    placeholder="Location"
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                </div>
                {/* FIX: error + validating messages moved OUTSIDE input-group */}
                {pincodeError && <small className="form-text text-danger">{pincodeError}</small>}
                {isValidatingPincode && <small className="form-text text-muted">Validating pincode...</small>}
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editQualification" className="form-label">
                  Qualification <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="editQualification"
                  name="qualification"
                  value={professionalFormData.qualification}
                  onChange={handleProfessionalFormChange}
                  required
                  disabled={isQualificationDisabled}
                >
                  <option value="">Select Qualification</option>
                  {qualificationOptions.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.name}
                    </option>
                  ))}
                </select>
                {isQualificationDisabled && (
                  <small className="form-text text-muted">Please select your profession first</small>
                )}
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editSpecialization" className="form-label">
                  Specialization
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="editSpecialization"
                  name="specialization"
                  value={professionalFormData.specialization}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter your specialization"
                  maxLength="100"
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editRegistrationNumber" className="form-label">
                  Registration Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="editRegistrationNumber"
                  name="registrationNumber"
                  value={professionalFormData.registrationNumber}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter registration number"
                  maxLength="30"
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editCvFile" className="form-label">
                  CV (Resume) <small className="text-muted">(Leave blank to keep current file)</small>
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="editCvFile"
                  name="cvFile"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'CV')}
                />
                {cvFile && (
                  <small className="form-text text-success d-block mt-1">
                    ✓ Selected: {cvFile.name} ({formatFileSize(cvFile.size)})
                  </small>
                )}
                <small className="form-text text-muted">Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)</small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editRegistrationCertificateFile" className="form-label">
                  Registration Certificate <small className="text-muted">(Leave blank to keep current file)</small>
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="editRegistrationCertificateFile"
                  name="registrationCertificateFile"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, 'Registration Certificate')}
                />
                {registrationCertificateFile && (
                  <small className="form-text text-success d-block mt-1">
                    ✓ Selected: {registrationCertificateFile.name} ({formatFileSize(registrationCertificateFile.size)})
                  </small>
                )}
                <small className="form-text text-muted">Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)</small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editExperience" className="form-label">
                  Experience (In Years) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="editExperience"
                  name="experience"
                  value={professionalFormData.experience}
                  onChange={handleProfessionalFormChange}
                  placeholder="Enter years of experience"
                  min="0"
                  max="70"
                  required
                />
              </div>

              <div className="col-md-12 p-2">
                <label htmlFor="editExperienceDetails" className="form-label">
                  Experience Details <span className="text-danger">*</span>
                </label>
                <ReactQuill
  theme="snow"
  placeholder="Enter your experience details"
  className="experience-details-editor"
  value={professionalFormData.experienceDetails}
  onChange={handleQuillChange}
  modules={{
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["link"],
      ["clean"]
    ]
  }}
  formats={[
    "header",
    "bold", "italic", "underline", "strike",
    "list", "bullet", "indent",
    "link"
  ]}
  style={{
    minHeight: "150px",
    marginBottom: "10px"
  }}
/>

                <small className="form-text text-muted">
                  <span id="editExperienceCount">{experienceCount}</span>/8000 characters
                </small>
              </div>

              
              <div className="col-md-6 p-2">
                <label htmlFor="editMobileNumber" className="form-label">
                  Mobile Number <span className="text-danger">*</span>
                </label>
               <div className="input-group">
    {/* Small Country Code */}
    <input
      type="text"
      className="form-control"
      style={{ maxWidth: "90px" }}
      id="editCountryCode"
      name="countryNumber"
      value={professionalFormData.countryNumber }
      onChange={handleProfessionalFormChange}
      placeholder="+91"
      maxLength={5}
      required
    />

    {/* Mobile */}
    <input
      type="tel"
      className="form-control"
      id="editMobileNumber"
      name="mobileNumber"
      value={professionalFormData.mobileNumber}
      onChange={handleProfessionalFormChange}
      onBlur={() => {
        const cur = (professionalFormData.mobileNumber || "").trim();
        const orig = (originalMobile || "").trim();
        if (cur.length === 10 && cur !== orig) validateContact("mobile");
      }}
      placeholder="Enter 10-digit mobile number"
      maxLength="10"
      pattern="[0-9]{10}"
      required
    />
  </div>
                {mobileError && <small className="form-text text-danger">{mobileError}</small>}
                {isValidatingContact && !mobileError && (
                  <small className="form-text text-muted">Validating mobile...</small>
                )}
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="editEmailId" className="form-label">
                  Email ID <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control form-control-display-only"
                  id="editEmailId"
                  name="emailId"
                  value={professionalFormData.emailId}
                  onChange={handleProfessionalFormChange}
                  onBlur={() => {
                    const cur = (professionalFormData.emailId || '').trim().toLowerCase();
                    const orig = (originalEmail || '').trim().toLowerCase();
                    if (cur && cur !== orig) validateContact('email');
                  }}
                  placeholder="Enter your email ID"
                  maxLength="50"
                  readOnly
                  disabled
                  required
                />
                {emailError && <small className="form-text text-danger">{emailError}</small>}
                {isValidatingContact && !emailError && (
                  <small className="form-text text-muted">Validating email...</small>
                )}
              </div>
           
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={handleCloseEditProfessionalModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting || isValidatingPincode || isValidatingContact}>
              {isSubmitting ? 'Updating...' : 'Update Professional'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}

export default RegisteredProfessionals;
